<template>
  <div class="task-management">
    <div class="header">
      <h2>任务管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleAddTask">
          <el-icon><Plus /></el-icon>
          创建公海任务
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索表单 -->
    <el-card class="search-card">
      <el-form :model="searchForm" label-width="80px" inline>
        <el-form-item label="任务标题">
          <el-input v-model="searchForm.title" placeholder="请输入任务标题" clearable />
        </el-form-item>
        <el-form-item label="任务类型">
          <el-select v-model="searchForm.taskType" placeholder="请选择任务类型" clearable>
            <el-option label="问卷调查" value="survey" />
            <el-option label="推广任务" value="promotion" />
            <el-option label="商家拓展" value="merchant_expansion" />
            <el-option label="内容创作" value="content" />
            <el-option label="测试任务" value="test" />
            <el-option label="其他任务" value="other" />
          </el-select>
        </el-form-item>
        <el-form-item label="任务来源">
          <el-select v-model="searchForm.taskSource" placeholder="请选择" clearable>
            <el-option label="平台任务" value="platform" />
            <el-option label="债权人发布" value="creditor" />
            <el-option label="商户发布" value="merchant" />
          </el-select>
        </el-form-item>
        <el-form-item label="公海/指定">
          <el-select v-model="searchForm.isPublic" placeholder="请选择" clearable>
            <el-option label="公海任务" :value="true" />
            <el-option label="指定任务" :value="false" />
          </el-select>
        </el-form-item>
        <el-form-item label="任务状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="已发布" value="published" />
            <el-option label="已接取" value="accepted" />
            <el-option label="已完成" value="completed" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 任务列表 -->
    <el-card>
      <el-table :data="taskList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="title" label="任务标题" width="180" show-overflow-tooltip />
        <el-table-column prop="taskType" label="任务类型" width="100">
          <template #default="{ row }">
            <el-tag size="small">{{ getTaskTypeText(row.taskType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="publisherType" label="发布者" width="100">
          <template #default="{ row }">
            <el-tag :type="getPublisherTypeTag(row.publisherType)" size="small">
              {{ getPublisherTypeText(row.publisherType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="difficultyLevel" label="难度" width="80">
          <template #default="{ row }">
            <el-tag :type="getDifficultyTag(row.difficultyLevel)" size="small">
              {{ getDifficultyText(row.difficultyLevel) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="rewardAmount" label="奖励积分" width="90">
          <template #default="{ row }">
            <span style="color: #e6a23c; font-weight: bold;">{{ row.rewardAmount || row.basePoints || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)" size="small">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 参与情况列 -->
        <el-table-column label="参与情况" width="130">
          <template #default="{ row }">
            <div v-if="row.participantStats">
              <el-tag size="small" type="info">参与{{ row.participantStats.total }}人</el-tag>
              <el-tag v-if="row.participantStats.pendingReview > 0" size="small" type="warning" style="margin-left: 4px;">
                待审{{ row.participantStats.pendingReview }}
              </el-tag>
            </div>
            <div v-else>
              <el-tag size="small" type="info">参与{{ row.participantCount || 0 }}人</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="160">
          <template #default="{ row }">
            {{ formatDateTime(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">查看</el-button>
            <el-button type="warning" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button 
              v-if="row.participantStats && row.participantStats.pendingReview > 0" 
              type="success" 
              size="small" 
              @click="handleReview(row)"
            >审核</el-button>
            <el-button v-if="row.status === 'published'" type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 创建公海任务对话框 -->
    <el-dialog v-model="createDialogVisible" :title="isEdit ? '编辑任务' : '创建公海任务'" width="900px" @close="resetCreateForm">
      <el-alert type="info" :closable="false" style="margin-bottom: 20px;">
        <template #title>
          <strong>公海任务说明：</strong>公海任务是指由后台创建的任务，所有用户都可以接单，无指定接单人。
        </template>
      </el-alert>
      <el-form ref="createFormRef" :model="createForm" :rules="createRules" label-width="100px">
        <el-form-item label="任务标题" prop="title">
          <el-input v-model="createForm.title" placeholder="请输入任务标题（5-50字）" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="任务描述" prop="description">
          <el-input v-model="createForm.description" type="textarea" :rows="4" placeholder="请详细描述任务内容、要求、完成标准等" maxlength="500" show-word-limit />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="任务类型" prop="taskType">
              <el-select v-model="createForm.taskType" placeholder="请选择" style="width: 100%">
                <el-option label="问卷调查" value="survey" />
                <el-option label="推广任务" value="promotion" />
                <el-option label="商家拓展" value="merchant_expansion" />
                <el-option label="内容创作" value="content" />
                <el-option label="测试任务" value="test" />
                <el-option label="其他任务" value="other" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="难度等级" prop="difficultyLevel">
              <el-select v-model="createForm.difficultyLevel" placeholder="请选择" style="width: 100%">
                <el-option label="简单" value="easy" />
                <el-option label="中等" value="medium" />
                <el-option label="困难" value="hard" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 目标数量（商家拓展任务） -->
        <el-form-item v-if="createForm.taskType === 'merchant_expansion'" label="目标数量" prop="targetCount">
          <el-input-number v-model="createForm.targetCount" :min="1" :max="100" />
          <span style="margin-left: 10px; color: #909399;">需要拓展的商家数量</span>
        </el-form-item>

        <!-- 奖励积分设置 -->
        <div style="border: 1px solid #e4e7ed; padding: 15px; border-radius: 4px; margin-bottom: 15px; background: #fafafa;">
          <el-form-item label="目标角色" prop="targetRoles">
            <el-checkbox-group v-model="createForm.targetRoles">
              <el-checkbox label="debtor">债务人</el-checkbox>
              <el-checkbox label="creditor">债权人</el-checkbox>
              <el-checkbox label="friend_debtor">亲友团(债务人)</el-checkbox>
              <el-checkbox label="friend_creditor">亲友团(债权人)</el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          
          <!-- 债务人积分设置 -->
          <div v-if="createForm.targetRoles.includes('debtor')" style="background: #fff; padding: 15px; margin-bottom: 15px; border-radius: 4px; border-left: 4px solid #409eff;">
            <div style="font-weight: bold; margin-bottom: 12px; color: #409eff;">债务人积分</div>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item label="现金分" label-width="70px">
                  <el-input-number v-model="createForm.debtorCashReward" :min="0" :max="10000" style="width: 100%" @change="calculateTotalReward" />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="解债分" label-width="70px">
                  <el-input-number v-model="createForm.debtorCreditReward" :min="0" :max="10000" style="width: 100%" @change="calculateTotalReward" />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="总积分" label-width="70px">
                  <el-tag type="success" size="large">{{ createForm.debtorCashReward + createForm.debtorCreditReward }} 积分</el-tag>
                </el-form-item>
              </el-col>
            </el-row>
          </div>
          
          <!-- 债权人积分设置 -->
          <div v-if="createForm.targetRoles.includes('creditor')" style="background: #fff; padding: 15px; margin-bottom: 15px; border-radius: 4px; border-left: 4px solid #e6a23c;">
            <div style="font-weight: bold; margin-bottom: 12px; color: #e6a23c;">债权人积分</div>
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="解债分" label-width="70px">
                  <el-input-number v-model="createForm.creditorReward" :min="0" :max="10000" style="width: 100%" @change="calculateTotalReward" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="总积分" label-width="70px">
                  <el-tag type="success" size="large">{{ createForm.creditorReward }} 积分</el-tag>
                </el-form-item>
              </el-col>
            </el-row>
          </div>
        </div>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="截止时间" prop="deadline">
              <el-date-picker v-model="createForm.deadline" type="datetime" placeholder="请选择截止时间" value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="预计耗时" prop="estimatedTime">
              <el-input v-model="createForm.estimatedTime" type="number" :min="0.5" step="0.5" placeholder="预计完成时间">
                <template #append>小时</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="任务位置" prop="location">
          <el-input v-model="createForm.location" placeholder="任务执行地点（可选）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleCreateTask" :loading="creating">{{ isEdit ? '保存' : '创建任务' }}</el-button>
      </template>
    </el-dialog>

    <!-- 查看任务详情对话框 -->
    <el-dialog v-model="viewDialogVisible" title="任务详情" width="700px">
      <el-descriptions :column="2" border v-if="currentTask">
        <el-descriptions-item label="任务ID">{{ currentTask.id }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="getStatusTag(currentTask.status)">{{ getStatusText(currentTask.status) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="任务标题" :span="2">{{ currentTask.title }}</el-descriptions-item>
        <el-descriptions-item label="任务类型">{{ getTaskTypeText(currentTask.taskType) }}</el-descriptions-item>
        <el-descriptions-item label="难度等级">
          <el-tag :type="getDifficultyTag(currentTask.difficultyLevel)">{{ getDifficultyText(currentTask.difficultyLevel) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="发布者类型">{{ getPublisherTypeText(currentTask.publisherType) }}</el-descriptions-item>
        <el-descriptions-item label="发布者">{{ currentTask.publisherName }}</el-descriptions-item>
        <el-descriptions-item label="奖励积分">{{ currentTask.rewardAmount || currentTask.basePoints || 0 }} 积分</el-descriptions-item>
        <el-descriptions-item label="参与情况">
          <span v-if="currentTask.participantStats">
            参与{{ currentTask.participantStats.total }}人 / 
            待审{{ currentTask.participantStats.pendingReview }}人 / 
            完成{{ currentTask.participantStats.completed }}人
          </span>
          <span v-else>参与{{ currentTask.participantCount || 0 }}人</span>
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDateTime(currentTask.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="截止时间">{{ currentTask.deadline ? formatDateTime(currentTask.deadline) : '不限' }}</el-descriptions-item>
        <el-descriptions-item label="任务描述" :span="2">{{ currentTask.description }}</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="viewDialogVisible = false">关闭</el-button>
        <el-button 
          v-if="currentTask && currentTask.participantStats && currentTask.participantStats.pendingReview > 0" 
          type="success" 
          @click="handleReview(currentTask); viewDialogVisible = false"
        >审核待处理</el-button>
      </template>
    </el-dialog>

    <!-- 审核弹窗 -->
    <el-dialog v-model="reviewDialogVisible" title="任务审核" width="900px">
      <el-alert type="warning" :closable="false" style="margin-bottom: 20px;">
        <template #title>以下用户提交了任务完成申请，请审核</template>
      </el-alert>

      <el-table :data="reviewParticipants" v-loading="reviewLoading" stripe border>
        <el-table-column prop="id" label="记录ID" width="80" />
        <el-table-column prop="userName" label="用户名" width="120" />
        <el-table-column prop="userMobile" label="手机号" width="120" />
        <el-table-column prop="userRole" label="角色" width="100">
          <template #default="{ row }">
            <el-tag size="small">{{ getRoleText(row.userRole) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="acceptedAt" label="接单时间" width="160">
          <template #default="{ row }">
            {{ formatDateTime(row.acceptedAt) }}
          </template>
        </el-table-column>
        <el-table-column prop="completedAt" label="提交时间" width="160">
          <template #default="{ row }">
            {{ formatDateTime(row.completedAt) }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="90">
          <template #default>
            <el-tag type="warning">待审核</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <el-button type="success" size="small" @click="handleApprove(row.id)">通过</el-button>
            <el-button type="danger" size="small" @click="handleReject(row.id)">拒绝</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="reviewParticipants.length === 0 && !reviewLoading" style="text-align: center; padding: 40px; color: #909399;">
        暂无待审核的任务完成申请
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'
import { taskApi } from '@/api/modules/task.js'

// 状态管理
const taskList = ref([])
const loading = ref(false)
const createDialogVisible = ref(false)
const viewDialogVisible = ref(false)
const creating = ref(false)
const currentTask = ref(null)
const createFormRef = ref()
const isEdit = ref(false)
const editTaskId = ref(null)

// 审核相关
const reviewDialogVisible = ref(false)
const reviewParticipants = ref([])
const reviewTaskId = ref(null)
const reviewLoading = ref(false)

// 搜索表单
const searchForm = reactive({ title: '', taskType: '', publisherType: '', taskCategory: '', status: '' })

// 创建任务表单
const createForm = reactive({
  title: '',
  description: '',
  taskType: 'other',
  difficultyLevel: 'medium',
  rewardAmount: 0,
  deadline: '',
  estimatedTime: '',
  location: '',
  targetCount: 1,
  targetRoles: ['debtor', 'creditor', 'friend_debtor', 'friend_creditor'],
  debtorCashReward: 0,
  debtorCreditReward: 0,
  creditorReward: 0,
  friendDebtorCashReward: 0,
  friendDebtorCreditReward: 0,
  friendDebtorHelperReward: 0,
  friendCreditorCashReward: 0,
  friendCreditorCreditReward: 0,
  friendCreditorHelperReward: 0
})

// 表单验证规则
const createRules = {
  title: [{ required: true, message: '请输入任务标题', trigger: 'blur' }],
  description: [{ required: true, message: '请输入任务描述', trigger: 'blur' }],
  taskType: [{ required: true, message: '请选择任务类型', trigger: 'change' }],
  difficultyLevel: [{ required: true, message: '请选择难度等级', trigger: 'change' }]
}

// 分页配置
const pagination = reactive({ current: 1, size: 10, total: 0 })

// 获取任务列表
const fetchTasks = async () => {
  try {
    loading.value = true
    const response = await taskApi.getTasks({ page: pagination.current, size: pagination.size, ...searchForm })
    if (response.code === 200 && response.data) {
      taskList.value = response.data.list
      pagination.total = response.data.total
    } else {
      if (response.code === 401) { localStorage.removeItem('admin-token'); localStorage.removeItem('admin-info'); window.location.href = '/#/login'; return }
      else ElMessage.error(response.message || '获取任务列表失败')
    }
  } catch (error) { ElMessage.error('获取任务列表异常') }
  finally { loading.value = false }
}

// 方法
const getTaskTypeText = (type) => ({ survey: '问卷调查', promotion: '推广任务', merchant_expansion: '商家拓展', content: '内容创作', test: '测试任务', other: '其他任务' }[type] || type)
const getPublisherTypeText = (type) => ({ creditor: '债权人', friend_group: '亲友团', merchant: '商户', company: '平台' }[type] || type)
const getPublisherTypeTag = (type) => ({ creditor: 'warning', friend_group: 'info', merchant: 'success', company: 'danger' }[type] || '')
const getDifficultyText = (difficulty) => {
  if (difficulty === 1 || difficulty === 'easy') return '简单'
  if (difficulty === 3 || difficulty === 'hard') return '困难'
  return '中等'
}
const getDifficultyTag = (difficulty) => {
  if (difficulty === 1 || difficulty === 'easy') return 'success'
  if (difficulty === 3 || difficulty === 'hard') return 'danger'
  return 'warning'
}
const getStatusText = (status) => ({ pending: '待处理', published: '已发布', assigned: '已分配', accepted: '已接取', completed: '已完成', cancelled: '已取消' }[status] || status)
const getStatusTag = (status) => ({ pending: 'warning', published: 'success', assigned: 'primary', accepted: 'primary', completed: 'success', cancelled: 'danger' }[status] || '')
const formatDateTime = (dateString) => { if (!dateString) return ''; return new Date(dateString).toLocaleString('zh-CN') }
const getRoleText = (role) => ({ debtor: '债务人', creditor: '债权人', friend_debtor: '亲友团(债务人)', friend_creditor: '亲友团(债权人)' }[role] || role)

const refreshData = () => { pagination.current = 1; fetchTasks() }
const handleSearch = () => { pagination.current = 1; fetchTasks() }
const resetSearch = () => { Object.keys(searchForm).forEach(key => { searchForm[key] = '' }); handleSearch() }
const handleSizeChange = (size) => { pagination.size = size; fetchTasks() }
const handleCurrentChange = (page) => { pagination.current = page; fetchTasks() }

// 计算总积分
const calculateTotalReward = () => {
  let maxReward = 0
  if (createForm.targetRoles.includes('debtor')) {
    maxReward = Math.max(maxReward, createForm.debtorCashReward + createForm.debtorCreditReward)
  }
  if (createForm.targetRoles.includes('creditor')) {
    maxReward = Math.max(maxReward, createForm.creditorReward)
  }
  createForm.rewardAmount = maxReward
}

const handleAddTask = () => {
  isEdit.value = false
  editTaskId.value = null
  resetCreateForm()
  createDialogVisible.value = true
}

const resetCreateForm = () => {
  if (createFormRef.value) createFormRef.value.resetFields()
  Object.assign(createForm, {
    title: '',
    description: '',
    taskType: 'other',
    difficultyLevel: 'medium',
    rewardAmount: 0,
    deadline: '',
    estimatedTime: '',
    location: '',
    targetCount: 1,
    targetRoles: ['debtor', 'creditor', 'friend_debtor', 'friend_creditor'],
    debtorCashReward: 0,
    debtorCreditReward: 0,
    creditorReward: 0,
    friendDebtorCashReward: 0,
    friendDebtorCreditReward: 0,
    friendDebtorHelperReward: 0,
    friendCreditorCashReward: 0,
    friendCreditorCreditReward: 0,
    friendCreditorHelperReward: 0
  })
}

const handleCreateTask = async () => {
  if (!createFormRef.value) return
  try {
    await createFormRef.value.validate()
    creating.value = true
    calculateTotalReward()
    
    const taskData = {
      title: createForm.title,
      description: createForm.description,
      type: createForm.taskType,
      taskType: createForm.taskType,
      difficultyLevel: createForm.difficultyLevel,
      rewardAmount: createForm.rewardAmount,
      deadline: createForm.deadline,
      estimatedTime: parseFloat(createForm.estimatedTime) || null,
      location: createForm.location,
      targetCount: createForm.targetCount,
      targetRoles: createForm.targetRoles,
      debtorCashReward: createForm.debtorCashReward,
      debtorCreditReward: createForm.debtorCreditReward,
      creditorReward: createForm.creditorReward,
      friendDebtorCashReward: createForm.friendDebtorCashReward,
      friendDebtorCreditReward: createForm.friendDebtorCreditReward,
      friendDebtorHelperReward: createForm.friendDebtorHelperReward,
      friendCreditorCashReward: createForm.friendCreditorCashReward,
      friendCreditorCreditReward: createForm.friendCreditorCreditReward,
      friendCreditorHelperReward: createForm.friendCreditorHelperReward
    }
    
    let response
    if (isEdit.value && editTaskId.value) {
      response = await taskApi.updateTask(editTaskId.value, taskData)
    } else {
      taskData.status = 'published'
      taskData.publisherType = 'company'
      taskData.taskCategory = 'company'
      response = await taskApi.createTask(taskData)
    }
    
    if (response.code === 200 || response.code === 201) {
      ElMessage.success(isEdit.value ? '任务已更新' : '公海任务创建成功')
      createDialogVisible.value = false
      resetCreateForm()
      fetchTasks()
    } else { ElMessage.error(response.message || '操作失败') }
  } catch (error) { console.log('表单验证失败:', error) }
  finally { creating.value = false }
}

const handleView = (row) => { currentTask.value = row; viewDialogVisible.value = true }

const handleEdit = (row) => {
  isEdit.value = true
  editTaskId.value = row.id
  createForm.title = row.title
  createForm.description = row.description
  createForm.taskType = row.taskType
  createForm.difficultyLevel = row.difficultyLevel === 1 ? 'easy' : (row.difficultyLevel === 3 ? 'hard' : 'medium')
  createForm.rewardAmount = row.rewardAmount || 0
  createForm.deadline = row.deadline || ''
  createForm.estimatedTime = row.estimatedDuration ? (row.estimatedDuration / 60).toString() : ''
  createForm.location = row.location || ''
  createForm.targetCount = row.targetCount || 1
  createForm.targetRoles = row.targetRoles ? row.targetRoles.split(',') : ['debtor', 'creditor', 'friend_debtor', 'friend_creditor']
  createForm.debtorCashReward = row.debtorCashReward || 0
  createForm.debtorCreditReward = row.debtorCreditReward || 0
  createForm.creditorReward = row.creditorReward || 0
  createForm.friendDebtorCashReward = row.friendDebtorCashReward || 0
  createForm.friendDebtorCreditReward = row.friendDebtorCreditReward || 0
  createForm.friendDebtorHelperReward = row.friendDebtorHelperReward || 0
  createForm.friendCreditorCashReward = row.friendCreditorCashReward || 0
  createForm.friendCreditorCreditReward = row.friendCreditorCreditReward || 0
  createForm.friendCreditorHelperReward = row.friendCreditorHelperReward || 0
  createDialogVisible.value = true
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(`确定要删除任务 "${row.title}" 吗？`, '删除确认', { type: 'warning' })
    await taskApi.deleteTask(row.id)
    ElMessage.success('任务已删除')
    fetchTasks()
  } catch (error) { 
    if (error !== 'cancel') ElMessage.error('删除任务失败') 
  }
}

// 获取任务参与记录
const fetchParticipants = async (taskId) => {
  reviewLoading.value = true
  try {
    const response = await taskApi.getTaskParticipants(taskId, { status: 'pending_review' })
    if (response.code === 200) {
      reviewParticipants.value = response.data.list
    }
  } catch (error) {
    ElMessage.error('获取参与记录失败')
  } finally {
    reviewLoading.value = false
  }
}

// 打开审核弹窗
const handleReview = async (row) => {
  reviewTaskId.value = row.id
  await fetchParticipants(row.id)
  reviewDialogVisible.value = true
}

// 审核通过
const handleApprove = async (acceptanceId) => {
  try {
    const response = await taskApi.reviewTask(reviewTaskId.value, {
      acceptanceId,
      approved: true
    })
    if (response.code === 200) {
      ElMessage.success('审核通过，积分已发放')
      await fetchParticipants(reviewTaskId.value)
      fetchTasks()
    } else {
      ElMessage.error(response.message || '审核失败')
    }
  } catch (error) {
    ElMessage.error('审核失败')
  }
}

// 审核拒绝
const handleReject = async (acceptanceId) => {
  try {
    await ElMessageBox.confirm('确定要拒绝该任务完成申请吗？', '审核确认', { type: 'warning' })
    const response = await taskApi.reviewTask(reviewTaskId.value, {
      acceptanceId,
      approved: false
    })
    if (response.code === 200) {
      ElMessage.success('已拒绝该申请')
      await fetchParticipants(reviewTaskId.value)
      fetchTasks()
    } else {
      ElMessage.error(response.message || '操作失败')
    }
  } catch (error) {
    if (error !== 'cancel') ElMessage.error('操作失败')
  }
}

onMounted(() => { fetchTasks() })
</script>

<style scoped>
.task-management { padding: 20px; }
.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.search-card { margin-bottom: 20px; }
.pagination { margin-top: 20px; display: flex; justify-content: flex-end; }
</style>
